// Full-fidelity per-patient export/import envelope (PATIENT_EXPORT_IMPORT_PLAN.md
// Phase 1, D1-D20). This module is deliberately PURE — no AWS SDK, no IO — so the
// same code builds the envelope in the API Lambda, validates it on import, and is
// exercised directly by `scripts/checkDataPortability.ts` with plain `tsx`.
//
// Adding an exported entity kind = a new member in EXPORT_SECTIONS + a collector
// call (api/src/lib/dynamo.ts collectFullPatientData) + a restorer call
// (restoreFullPatientData) + a CSV column set here.

import type { MedicalEvent, ChatSession, ChatMessage } from "./types.js";
import type { Regimen } from "./regimen.js";

/** Bumped whenever the envelope's structural shape changes. `validateImportEnvelope`
 *  accepts anything <= this (forward-compatible read), rejects anything greater. */
export const DATA_PORTABILITY_SCHEMA_VERSION = 2;

/** Discriminator stamped on every envelope so a stray JSON file can't be imported. */
export const DATA_PORTABILITY_KIND = "medassist.patient-export" as const;

/** Every top-level section name in the envelope. Order is stable so the analyst
 *  bundle emits files in a predictable sequence. */
export const EXPORT_SECTIONS = [
  "events",
  "regimens",
  "chatSessions",
  "feedback",
  "gymExercises",
  "gymUsage",
  "gymRecommendation",
  "foodItems",
  "foodUsage",
  "conditions",
  "symptomAlerts",
] as const;
export type ExportSection = (typeof EXPORT_SECTIONS)[number];

/** One restored chat session with its full transcript (seq order preserved). */
export interface ExportChatSession {
  session: ChatSession;
  messages: ChatMessage[];
}

export interface PatientExportSections {
  /** ALL event types, unfiltered, including soft-deleted (deleted/deletedAt kept). */
  events: MedicalEvent[];
  /** Includes archived (inactive) regimens. */
  regimens: Regimen[];
  chatSessions: ExportChatSession[];
  /** `exportFeedbackForPatient()` output verbatim: `{ item, snapshot }[]`. */
  feedback: unknown[];
  gymExercises: unknown[];
  gymUsage: unknown[];
  gymRecommendation: unknown | null;
  foodItems: unknown[];
  foodUsage: unknown[];
  conditions: unknown[];
  symptomAlerts: unknown[];
}

export interface PatientExportEnvelope {
  schemaVersion: number;
  kind: typeof DATA_PORTABILITY_KIND;
  /** The S3 export UUID — doubles as the import re-run guard key (D6). */
  exportId: string;
  exportedAt: string;
  /** Informational only; the importer mints a fresh patientId (D3/D4). */
  sourcePatientId: string;
  patientName: string;
  app: { deployment: string };
  options: { includePhotos: boolean; includeAnalystBundle: boolean };
  /** Full Patient record minus PK/SK/entityType. */
  profile: Record<string, unknown>;
  sections: PatientExportSections;
  /** Binary assets present only in a .zip export; [] for data-only JSON. */
  assets: ExportAssetRef[];
}

export interface ExportAssetRef {
  section: ExportSection;
  /** eventId / userExerciseId / photoId / … */
  recordId: string;
  kind: "photo" | "photo-thumb" | "pt-sheet";
  /** Path inside the .zip, e.g. "assets/bodyphotos/<photoId>/front.jpg". */
  archivePath: string;
  /** Original S3 key, informational. */
  sourceS3Key: string;
  contentType: string;
}

/** Thrown by the pure transforms on malformed input. The caller (restore) counts
 *  a thrown record as `skipped` rather than aborting the whole import. */
export class DataPortabilityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DataPortabilityError";
  }
}

// ---------------------------------------------------------------------------
// Envelope build + validate
// ---------------------------------------------------------------------------

export interface BuildExportEnvelopeInput {
  exportId: string;
  exportedAt: string;
  sourcePatientId: string;
  patientName: string;
  deployment: string;
  options: { includePhotos: boolean; includeAnalystBundle: boolean };
  profile: Record<string, unknown>;
  sections: Partial<PatientExportSections>;
  assets?: ExportAssetRef[];
}

/** Assembles the envelope from already-fetched parts. Stamps version/kind, echoes
 *  `options`, and guarantees every EXPORT_SECTIONS key is present. Never does IO. */
export function buildExportEnvelope(input: BuildExportEnvelopeInput): PatientExportEnvelope {
  const s = input.sections;
  return {
    schemaVersion: DATA_PORTABILITY_SCHEMA_VERSION,
    kind: DATA_PORTABILITY_KIND,
    exportId: input.exportId,
    exportedAt: input.exportedAt,
    sourcePatientId: input.sourcePatientId,
    patientName: input.patientName,
    app: { deployment: input.deployment },
    options: {
      includePhotos: !!input.options.includePhotos,
      includeAnalystBundle: !!input.options.includeAnalystBundle,
    },
    profile: input.profile,
    sections: {
      events: s.events ?? [],
      regimens: s.regimens ?? [],
      chatSessions: s.chatSessions ?? [],
      feedback: s.feedback ?? [],
      gymExercises: s.gymExercises ?? [],
      gymUsage: s.gymUsage ?? [],
      gymRecommendation: s.gymRecommendation ?? null,
      foodItems: s.foodItems ?? [],
      foodUsage: s.foodUsage ?? [],
      conditions: s.conditions ?? [],
      symptomAlerts: s.symptomAlerts ?? [],
    },
    assets: input.assets ?? [],
  };
}

export type ValidateImportEnvelopeResult =
  | { ok: true; envelope: PatientExportEnvelope }
  | { ok: false; error: string };

/** Structural gate on an imported envelope. Rejects: not an object; wrong `kind`;
 *  `schemaVersion` missing or newer than this reader; `profile` missing/not an
 *  object; `sections` missing. Accepts an older `schemaVersion` (forward-compatible). */
export function validateImportEnvelope(x: unknown): ValidateImportEnvelopeResult {
  if (!x || typeof x !== "object" || Array.isArray(x)) {
    return { ok: false, error: "envelope is not an object" };
  }
  const env = x as Record<string, unknown>;
  if (env.kind !== DATA_PORTABILITY_KIND) {
    return { ok: false, error: `unexpected kind: ${String(env.kind)}` };
  }
  if (typeof env.schemaVersion !== "number" || Number.isNaN(env.schemaVersion)) {
    return { ok: false, error: "schemaVersion missing or not a number" };
  }
  if (env.schemaVersion > DATA_PORTABILITY_SCHEMA_VERSION) {
    return {
      ok: false,
      error: `schemaVersion ${env.schemaVersion} is newer than this app supports (${DATA_PORTABILITY_SCHEMA_VERSION})`,
    };
  }
  if (!env.profile || typeof env.profile !== "object" || Array.isArray(env.profile)) {
    return { ok: false, error: "profile missing or not an object" };
  }
  if (!env.sections || typeof env.sections !== "object" || Array.isArray(env.sections)) {
    return { ok: false, error: "sections missing" };
  }
  return { ok: true, envelope: x as PatientExportEnvelope };
}

// ---------------------------------------------------------------------------
// ID rewrite (D4 — only the partition patientId changes; every record ID is kept)
// ---------------------------------------------------------------------------

/** Global literal substring replace (no regex — record IDs contain characters
 *  that would otherwise need escaping). */
function replaceAll(haystack: string, from: string, to: string): string {
  return haystack.split(from).join(to);
}

function rewriteRefString(s: string, oldId: string, newId: string): string {
  return replaceAll(s, `/${oldId}/`, `/${newId}/`);
}

/**
 * Shallow-clones a record, swapping a top-level `patientId` to `newId` and
 * rewriting `/<oldId>/` -> `/<newId>/` inside any `sourceRefs[]`, `payload.notesRef`,
 * and `payload.output.markdownS3Key` string. All other IDs are untouched (D4).
 */
export function rewritePatientId<T extends Record<string, unknown>>(
  record: T,
  oldId: string,
  newId: string
): T {
  const clone: Record<string, unknown> = { ...record };

  if (typeof clone.patientId === "string") {
    clone.patientId = newId;
  }

  if (Array.isArray(clone.sourceRefs)) {
    clone.sourceRefs = clone.sourceRefs.map((r) =>
      typeof r === "string" && oldId ? rewriteRefString(r, oldId, newId) : r
    );
  }

  if (clone.payload && typeof clone.payload === "object" && !Array.isArray(clone.payload)) {
    const payload: Record<string, unknown> = { ...(clone.payload as Record<string, unknown>) };
    if (typeof payload.notesRef === "string" && oldId) {
      payload.notesRef = rewriteRefString(payload.notesRef, oldId, newId);
    }
    if (payload.output && typeof payload.output === "object" && !Array.isArray(payload.output)) {
      const output: Record<string, unknown> = { ...(payload.output as Record<string, unknown>) };
      if (typeof output.markdownS3Key === "string" && oldId) {
        output.markdownS3Key = rewriteRefString(output.markdownS3Key, oldId, newId);
      }
      payload.output = output;
    }
    clone.payload = payload;
  }

  return clone as T;
}

/**
 * Prepares an imported event for `putEvent`: rewrites the partition patientId,
 * forces `source: "import_csv"` + the importer's `actorUserId`, and preserves
 * `eventId` / `occurredAt` / `deleted` / `deletedAt` / `payload`. Throws a
 * DataPortabilityError when a required field is missing or malformed.
 */
export function normalizeImportedEvent(
  e: unknown,
  newPatientId: string,
  actorUserId: string
): MedicalEvent {
  if (!e || typeof e !== "object" || Array.isArray(e)) {
    throw new DataPortabilityError("event is not an object");
  }
  const raw = e as Record<string, unknown>;
  if (typeof raw.eventId !== "string" || !raw.eventId) {
    throw new DataPortabilityError("event.eventId missing");
  }
  if (typeof raw.eventType !== "string" || !raw.eventType) {
    throw new DataPortabilityError(`event ${raw.eventId}: eventType missing`);
  }
  if (typeof raw.occurredAt !== "string" || !raw.occurredAt) {
    throw new DataPortabilityError(`event ${raw.eventId}: occurredAt missing`);
  }
  if (!raw.payload || typeof raw.payload !== "object" || Array.isArray(raw.payload)) {
    throw new DataPortabilityError(`event ${raw.eventId}: payload missing or not an object`);
  }

  const oldId = typeof raw.patientId === "string" ? raw.patientId : "";
  const rewritten = rewritePatientId(raw, oldId, newPatientId);
  return {
    ...(rewritten as unknown as MedicalEvent),
    patientId: newPatientId,
    source: "import_csv",
    actorUserId,
  };
}

// ---------------------------------------------------------------------------
// Analyst bundle: CSV + NDJSON (D7 — export-only, re-import always uses the JSON)
// ---------------------------------------------------------------------------

/** Generic columns every event CSV row carries. `payloadJson` keeps the lossless payload. */
const GENERIC_EVENT_COLUMNS = [
  "eventId",
  "eventType",
  "occurredAt",
  "recordedAt",
  "source",
  "actorUserId",
  "deleted",
  "payloadJson",
] as const;

/** The event types common enough to get their own friendly analyst CSV. */
export const EXPORT_EVENT_CSV_TYPES = [
  "medication_taken",
  "symptom",
  "headache",
  "vital_sign",
  "sleep",
  "food",
  "hydration",
  "bowel_movement",
] as const;

/** Column order per CSV file (D7). `*Json` columns hold the lossless serialization
 *  of the record (`payloadJson` = the event payload; `recordJson` = the whole row). */
export const SECTION_CSV_COLUMNS: Record<string, string[]> = {
  events: [...GENERIC_EVENT_COLUMNS],
  regimens: [
    "regimenId",
    "name",
    "kind",
    "scheduledGroup",
    "active",
    "createdAt",
    "updatedAt",
    "recordJson",
  ],
  chatSessions: ["sessionId", "title", "startedAt", "lastMessageAt", "mode", "messageCount"],
  feedback: ["feedbackId", "sessionId", "rating", "reasonCategory", "createdAt", "recordJson"],
  gymExercises: ["userExerciseId", "name", "equipmentType", "createdAt", "recordJson"],
  gymUsage: ["userExerciseId", "logCount", "lastLoggedAt", "lastSide", "recordJson"],
  gymRecommendation: ["generatedAt", "summary", "recordJson"],
  foodItems: ["userFoodId", "name", "normalizedName", "source", "createdAt", "recordJson"],
  foodUsage: ["userFoodId", "logCount", "lastLoggedAt", "recordJson"],
  conditions: ["conditionId", "name", "archived", "createdAt", "recordJson"],
  symptomAlerts: ["key", "name", "eventType", "currentSeverity", "evaluatedAt", "recordJson"],
  // Friendly per-type event CSVs — generic envelope columns plus the payload blob.
  ...Object.fromEntries(
    EXPORT_EVENT_CSV_TYPES.map((t) => [t, [...GENERIC_EVENT_COLUMNS]] as const)
  ),
};

function dottedGet(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && !Array.isArray(acc)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function cellValue(record: Record<string, unknown>, column: string): string {
  if (column === "payloadJson") {
    return JSON.stringify(record.payload ?? {});
  }
  if (column === "recordJson") {
    return JSON.stringify(record);
  }
  if (column === "messageCount") {
    const msgs = record.messages;
    return Array.isArray(msgs) ? String(msgs.length) : "0";
  }
  // chatSessions records are `{ session, messages }` — resolve plain columns
  // against the nested session first, then the record itself.
  let v = dottedGet(record, column);
  if (v === undefined && record.session && typeof record.session === "object") {
    v = dottedGet(record.session as Record<string, unknown>, column);
  }
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

/** Header row + one row per record. Unknown sections fall back to the generic
 *  event columns. Cells are raw strings — call `serializeCsv` to RFC-4180 encode. */
export function toCsvRows(section: string, records: Array<Record<string, unknown>>): string[][] {
  const columns = SECTION_CSV_COLUMNS[section] ?? SECTION_CSV_COLUMNS.events;
  const rows: string[][] = [columns.slice()];
  for (const record of records) {
    rows.push(columns.map((c) => cellValue(record, c)));
  }
  return rows;
}

/** RFC-4180: quote a field when it contains a comma, quote, CR or LF; double any
 *  embedded quote. Rows are joined with CRLF. */
export function serializeCsv(rows: string[][]): string {
  const encodeCell = (cell: string): string =>
    /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  return rows.map((row) => row.map(encodeCell).join(",")).join("\r\n");
}

/** Lossless newline-delimited JSON — one record per line. */
export function toNdjson(records: unknown[]): string {
  return records.map((r) => JSON.stringify(r)).join("\n");
}
